import type { ArrangementShape } from "./arrangement-shape.js";
// Wire types shared between the extension (src/) and the Worker (workers/).
// Single source of truth for the Seedbed API's request/response shapes and the
// JamRoom envelope. The jam *payload* (JamBatch) stays in src/protocol.ts —
// only the transport envelope lives here.

import type { CanonicalClipState, CanonicalTrackState, DeviceState } from "./sync/state.js";
import type { ClipData } from "./sync/protocol.js";
import type { HydrationSkipped } from "./sync/hydrate.js";
import type { UploadSourceFormat } from "./seed-history.js";
import type { WireNote } from "./midi/notes.js";

// ---- Auth -------------------------------------------------------------------

export interface AuthUser {
  id: string;
  username: string;
  email: string;
  /** Public profile URL name (`/@handle`). Every auth response carries it
   * from 0010 on; a session stored before then may not. */
  handle?: string;
}

export interface AuthTokens {
  accessToken: string;
  /** ISO timestamp the access token expires at (client refreshes ahead of it). */
  accessExpiresAt: string;
  /** Opaque "<id>.<secret>" pair; rotated on every refresh. */
  refreshToken: string;
}

export interface AuthResponse extends AuthTokens {
  user: AuthUser;
}

export interface SignUpRequest {
  email: string;
  password: string;
  username: string;
}

export interface SignInRequest {
  email: string;
  password: string;
}

export interface RefreshRequest {
  refreshToken: string;
}

// ---- Seeds ------------------------------------------------------------------

export interface ApiStem {
  id: string;
  name: string;
  /** Signed, time-limited download URL (not the object key). */
  audioUrl: string;
  /**
   * Signed URL of the MP3 preview rendition, when one exists — what a play
   * button should load. `audioUrl` stays the original, for import.
   */
  previewUrl?: string;
  /**
   * The R2 key behind `audioUrl`. The web app streams a stem with no preview
   * through its own worker by key, which rewraps the AIFF Live renders
   * (stored under `.wav`) as a WAV Chrome can play.
   */
  key?: string;
  trackIndex: number;
}

export interface ApiProducer {
  id: string;
  username: string;
  avatarUrl: string | null;
  /** Public profile URL name (`/@handle`), from 0010. */
  handle?: string;
}

/** Who may see a seed. `unlisted`: anyone with its link, never in a feed. */
export type SeedVisibility = "public" | "unlisted" | "private";

/**
 * Feed row: everything the browse carousel needs, WITHOUT the arrangement and
 * changelog blobs (fetch GET /api/seeds/:id for those before import).
 */
export interface SeedSummary {
  id: string;
  title: string;
  bpm: number;
  key: string | null;
  genre: string | null;
  description: string | null;
  masterUrl: string;
  /** Signed URL of the master's MP3 preview, when one exists (see ApiStem.previewUrl). */
  masterPreviewUrl?: string;
  stemsZipUrl: string | null;
  /**
   * The one edge of the tree. A seed is a commit with ONE parent — "a new take
   * by me" and "a remix by you" are the same operation, a child. Null on a root.
   */
  parentSeedId: string | null;
  /** The jam this seed was committed from, if any (see JamSummary). */
  jamId: string | null;
  /** The commit message: what changed, or why this fork exists. */
  note: string | null;
  createdAt: string;
  /** Cached root of this seed's tree — every node of one tree shares it. */
  rootSeedId: string;
  /** Depth + 1, a display number. Siblings share it; nothing keys on it. */
  version: number;
  producer: ApiProducer;
  stems: ApiStem[];
  stemCount: number;
  /** Direct children (complete only). */
  childCount: number;
  /**
   * How many OTHER PRODUCERS built on any of this producer's takes on this
   * tree, however many generations down — the same number a feed card carries.
   * People, not rows: a remixer's second pass at their own remix is still one
   * remix of this song. On the detail route and the "mine" list.
   */
  remixCount?: number;
  // ---- 0010, the web app's social layer. Optional so older clients' own
  // fixtures still type-check — the API always sends them.
  visibility?: SeedVisibility;
  playCount?: number;
  likeCount?: number;
  commentCount?: number;
  /** Whether the signed-in caller likes this seed. False when signed out. */
  likedByMe?: boolean;
  /** Whether the signed-in caller follows this seed's producer. False when
   * signed out — a Follow button on a seed page renders from this. */
  producerFollowedByMe?: boolean;
  /** Master peaks: base64 of 256 bytes, 0–255. Null until one is recorded. */
  waveform?: string | null;
  /** The master's length, recorded with the waveform. */
  durationMs?: number | null;
  /** The master's R2 key — what the web app streams through its own worker. */
  masterKey?: string | null;
  /** Who else made it (seed_collaborators): a jam's other players. */
  collaborators?: ApiProducer[];
  /**
   * The arrangement reduced to what a picture needs (see arrangement-shape.ts).
   * Sent on the LIST so a client never fetches each seed's full detail just to
   * draw it. Null when the seed has no drawable clips; absent where the route
   * does not compute it.
   */
  shape?: ArrangementShape | null;
  /**
   * What this seed is a remix OF — set only when it grew from someone else's
   * seed. Not simply the parent: a producer's later takes on a remix keep the
   * credit, so this is the nearest ancestor by ANOTHER producer (in this model,
   * the parent of this producer's earliest take on the tree). Null on an
   * original and on your own next version. Absent where the route does not
   * compute it.
   */
  remixOf?: RemixOrigin | null;
}

/**
 * The seed a remix grew from, enough to credit it and play the two back to
 * back. Carried by feed cards and by seed detail.
 */
export interface RemixOrigin {
  id: string;
  title: string;
  producer: ApiProducer;
  /** The original's audio, for playing the two side by side. "" with none. */
  audioUrl: string;
}

/** Full detail: summary plus the heavy JSON blobs. */
export interface SeedApiDetail extends SeedSummary {
  /** JSON-parsed Snapshot (see src/inspect/snapshot.ts). */
  arrangement: unknown | null;
  /** JSON-parsed DiffLogEntry[] (see src/sync/changelog.ts). */
  changelog: unknown[];
  /**
   * Signed GET per DISTINCT clip audio key in the arrangement, for seeds that
   * ship per-clip audio instead of stems. Empty on stem-shaped seeds.
   *
   * Signed here for the same reason stems are: a client should never have to
   * mint a URL, and a key that is signed at read time cannot go stale in
   * storage the way a stored URL did (2026-08-13).
   */
  clipAudioUrls?: Record<string, string>;
  /**
   * Signed preview URL per clip audio key that HAS a preview rendition, keyed
   * by the ORIGINAL key exactly as `clipAudioUrls` is. Absent keys have none:
   * play the original.
   */
  clipPreviewUrls?: Record<string, string>;
}

// ---- Producer library -------------------------------------------------------

/**
 * One distinct clip across every seed a user uploaded, collapsed across
 * seeds and bounce generations. Copies merge by content tag (byte-identical
 * audio) or bounce-normalized name WITHIN the same track identity (the
 * stamped ⟨sb:…⟩ token, with a name fallback — shared/library.ts) — a jam
 * re-bounces the same clip into a NEW object on every commit, so tag
 * identity alone would list one cowbell twenty-one times, and name identity
 * across tracks would merge two different "Kick"s.
 */
/**
 * Everything needed to PLACE a library clip into a room: the newest copy's
 * own R2 key (an `upsertClip` create must name a real object — the room
 * HEAD-checks it, and cross-seed keys pass because publish copies whatever a
 * room references) plus its full wire geometry. `clip.startTime` is carried
 * verbatim from the source arrangement and is meaningless at the destination
 * — placers always override it.
 */
export interface LibraryPlacement {
  audioKey: string;
  clip: ClipData;
}

export interface LibraryClipEntry {
  /** Normalized display name (bounce/render decorations stripped); null for
   * a legacy full-length stem render. */
  name: string | null;
  /** Token-stripped display name of the lane it lives on. */
  trackName: string;
  /** A clip's own content-addressed object, or a legacy per-track stem. */
  source: "stem" | "clip";
  /** Content tag of the newest copy (from the clip key's filename); null for
   * stems. */
  tag: string | null;
  /** The newest copy. Signed GET (API) or raw R2 key (viz worker — its proxy
   * streams keys). */
  previewUrl: string;
  /** Live clip color of the newest copy; null for stems. */
  color: number | null;
  /** Clip length in beats (newest copy); null for stems. */
  durationBeats: number | null;
  /** How many seeds carry some copy of this clip. */
  seedCount: number;
  latestSeedId: string;
  latestSeedTitle: string;
  latestSeedCreatedAt: string;
  /** Key + geometry of the newest copy, for placing this clip into a room.
   * Absent on stems and on anything else with no placeable audio object. */
  placement?: LibraryPlacement;
}

/** GET /api/me/library — the caller's distinct clips, newest first. */
export interface LibraryResponse {
  clips: LibraryClipEntry[];
  /** True when an item or seed cap was hit and older seeds were dropped. */
  truncated: boolean;
}

// ---- Reference: clips from every seed the producer has touched ----------------
//
// The web app's library page lists, beside Mine, the clips of every seed the
// producer uploaded, remixed from, downloaded, liked or jammed on — a place
// to go back to for a sound without reopening the seed. Read-only: a clip
// found here is KEPT into Mine (POST /api/library/clips/keep) to be organised.

/** How the producer came to this seed; a seed may have several. */
export type ReferenceHow = "uploaded" | "remixed" | "downloaded" | "liked" | "jammed";

export interface ReferenceClip {
  /** The clip's name in the seed, or the stem's; never empty. */
  name: string;
  /** Token-stripped display name of the lane it sits on. */
  trackName: string;
  trackIndex: number;
  source: "stem" | "clip";
  /** Live clip colour; null for a stem. */
  color: number | null;
  /** Length in beats at the seed's tempo; null for a stem. */
  durationBeats: number | null;
  /** The object's R2 key. A pool key (`…blobs/<sha256>.wav`) can be kept
   * into Mine; a legacy per-seed key can only be heard. */
  audioKey: string;
  /** Signed GET (2 h). */
  previewUrl: string;
}

/** A MIDI clip of a seed, notes and all, so it can be heard and kept as an idea. */
export interface ReferenceMidi {
  /** Stable within the seed: the lane and the notes' digest. */
  key: string;
  name: string;
  trackName: string;
  trackIndex: number;
  color: number;
  durationBeats: number;
  noteCount: number;
  notes: WireNote[];
}

export interface ReferenceSeed {
  id: string;
  title: string;
  bpm: number;
  key: string | null;
  producer: ApiProducer;
  createdAt: string;
  /** When the producer last touched it (the newest of the ways in). */
  touchedAt: string;
  how: ReferenceHow[];
  clips: ReferenceClip[];
  /** The seed's MIDI clips, the first few dozen; absent on responses from before. */
  midi?: ReferenceMidi[];
}

/** GET /api/me/reference — newest touch first. */
export interface ReferenceLibraryResponse {
  seeds: ReferenceSeed[];
  /** True when a seed or clip cap was hit and older seeds were dropped. */
  truncated: boolean;
}

/** POST /api/library/clips/keep — a seed's clip copied into Mine. The bytes
 * are already in the pool; the library takes its own reference. */
export interface KeepClipRequest {
  /** The seed the clip is in — or, for a clip a jam holds, the jam. One of the two. */
  seedId?: string;
  jamId?: string;
  audioKey: string;
  /** From a jam: what the room knows of the clip, since the jam's state is
   * not on the server. The seed path reads these from the seed instead. */
  bpm?: number | null;
  kind?: MyClipKind;
  /** The folder it lands in; absent or null = the root. */
  folderId?: string | null;
  /** A name of the producer's choosing; the clip's own by default. */
  name?: string;
}

/** The clip now in Mine — `already` when the same audio was there before. */
export interface KeepClipResponse {
  clip: MyClip;
  already: boolean;
  usedBytes: number;
  quotaBytes: number;
}

// ---- Pool previews -----------------------------------------------------------

/**
 * `POST /api/audio/previews`: which of these pool blobs have an MP3 preview
 * (shared/blobs.ts `previewKeyOf`). A room asks once for every blob it draws
 * and requests `/api/audio/blob/:hash/preview` only where one exists, instead
 * of probing each and taking a 404 for every blob uploaded without one.
 * Hashes that are not sha-256 hex are ignored; duplicates are answered once.
 */
export interface PoolPreviewsRequest {
  hashes: string[];
}

/** The subset of the asked hashes that have a preview, in no particular order. */
export interface PoolPreviewsResponse {
  hashes: string[];
}

/** Most hashes one `POST /api/audio/previews` takes; a client chunks past it. */
export const POOL_PREVIEWS_MAX = 500;

// ---- Uploads ----------------------------------------------------------------

export interface UploadFileManifest {
  /**
   * `preview` is an MP3 rendition of one of the OTHER entries — small enough
   * to answer a play button in well under a second — and is planned as that
   * original's `.mp3` sibling (shared/blobs.ts `previewKeyOf`). A preview
   * entry carries `previewOf`, never `hash`: it is not content-addressed.
   */
  kind: "stem" | "master" | "clip" | "preview";
  /** Display name (stems only). */
  name?: string;
  trackIndex?: number;
  sizeBytes: number;
  /**
   * `kind: "preview"` only: the `hash` of the pool object this previews. The
   * same manifest must carry that original (as a clip or the master) — a
   * preview for bytes this upload does not itself name is refused.
   */
  previewOf?: string;
  /**
   * Full sha-256 hex of the bytes (clips and the master). Present ⇒ the file
   * lands in the shared blob pool (`blobs/<hash>.wav`, shared/blobs.ts), where
   * the same bytes are stored once across every seed, version and jam — and
   * the plan comes back `exists: true` when the pool already holds them.
   * Absent ⇒ the legacy per-seed key (older extension builds).
   */
  hash?: string;
  /**
   * @deprecated Legacy 8-hex content tag for `kind: "clip"`, only read when
   * `hash` is absent: it names the object under the seed's own prefix.
   */
  tag?: string;
}

/**
 * Where a seed was made. `room` is a commit from a jam room's state (the
 * browser editor's Save, or a jam's auto-seal); the rest are /api/uploads
 * callers. Stored on `seeds.origin` (migration 0014).
 */
export type SeedOrigin = "ableton" | "audiotool" | "web_upload" | "room";

export const SEED_ORIGINS: readonly SeedOrigin[] = ["ableton", "audiotool", "web_upload", "room"];

export function isSeedOrigin(value: unknown): value is SeedOrigin {
  return typeof value === "string" && (SEED_ORIGINS as readonly string[]).includes(value);
}

export interface CreateUploadRequest {
  title: string;
  bpm: number;
  key: string;
  genre: string | null;
  description: string | null;
  /**
   * Branch off this seed. Null starts a new root. The server checks the parent
   * exists and is complete, and derives root/version from it — the client
   * never computes lineage.
   */
  parentSeedId: string | null;
  /**
   * Commit to this jam: the parent becomes the jam's CURRENT tip (server-owned;
   * a `parentSeedId` that disagrees is a 409) and the tip advances when the
   * upload completes.
   */
  jamId: string | null;
  /** Commit message. */
  note: string | null;
  /** DiffLogEntry[] — stored verbatim on the seed row. */
  changelog: unknown[];
  /** Snapshot — stored verbatim on the seed row. */
  arrangement: unknown | null;
  files: UploadFileManifest[];
  /**
   * Where this upload came from. Optional: extension builds from before the
   * column send none, and the server infers ableton or audiotool from the
   * arrangement (see workers/src/uploads.ts originOf).
   */
  origin?: SeedOrigin;
}

export interface UploadFilePlan {
  kind: "stem" | "master" | "clip" | "preview";
  name?: string;
  trackIndex?: number;
  /** Echoed back for clips, so the uploader can pair a key to its content tag. */
  tag?: string;
  /** Echoed back for pool files — the pairing key when `hash` was sent. */
  hash?: string;
  /** Echoed back for previews — the pairing key when `previewOf` was sent. */
  previewOf?: string;
  /**
   * True ⇒ the pool already holds these bytes, VERIFIED: no URLs are minted
   * and the uploader must not PUT. The key is still the clip's `audioKey`.
   * For a preview: a rendition already exists for that original (first
   * writer wins), so the uploader must not PUT one either.
   */
  exists?: boolean;
  /** Object key inside the bucket. */
  key: string;
  /**
   * Presigned PUT URL. Every object goes up in ONE PUT — R2 takes 5 GiB in
   * one, and the server refuses anything over its pool ceiling (256 MB,
   * workers/src/blobs.ts VERIFY_MAX_BYTES) at plan time, so there is no
   * multipart any more.
   */
  putUrl?: string;
  /**
   * Headers the PUT must carry byte for byte: they are signed into `putUrl`,
   * so a PUT without them is refused. A pool object's is
   * `x-amz-checksum-sha256`, the hash its key is named by — R2 compares the
   * body against it before the object exists, which is what lets /complete
   * trust the bytes without reading them back.
   */
  putHeaders?: Record<string, string>;
}

export interface CreateUploadResponse {
  seedId: string;
  /** The parent the server recorded — the jam's tip when `jamId` was sent. */
  parentSeedId: string | null;
  files: UploadFilePlan[];
}

export interface CompletedFilePart {
  partNumber: number;
  etag: string;
}

export interface CompleteUploadRequest {
  files: Array<{
    key: string;
    /** @deprecated No plan issues multipart parts any more; still accepted from an extension that planned one before the change. */
    uploadId?: string;
    /** @deprecated See `uploadId`. */
    parts?: CompletedFilePart[];
  }>;
  /** Stem row metadata (order defines <seedId>_st<n> ids). */
  stems: Array<{ key: string; name: string; trackIndex: number }>;
  /**
   * The master's key. Pool masters are named by hash, so the server can no
   * longer find the master by its `/master.wav` suffix; legacy uploads may
   * omit this and keep the suffix rule.
   */
  masterKey?: string;
  /**
   * Which stored object holds each clip's audio, for seeds that ship per-clip
   * audio. The server stamps these onto the arrangement snapshot it already
   * holds, addressing clips by position.
   *
   * Sent here rather than in the manifest because object keys are minted
   * server-side (they carry the channel prefix), so they do not exist yet when
   * the arrangement is first written.
   */
  clipAudio?: Array<{ trackIndex: number; clipIndex: number; key: string }>;
  /**
   * Same as `clipAudio`, addressing `arrangement.midiTracks[t].arrangementClips[c]`
   * — the optional bounce of a MIDI clip. Its own address space because MIDI
   * lanes live in a parallel collection (see Snapshot.midiTracks).
   */
  midiClipAudio?: Array<{ trackIndex: number; clipIndex: number; key: string }>;
  /**
   * Same again for Session View audio clips, addressing
   * `arrangement.audioTracks[t].sessionClips[c]` (clipIndex is the index in
   * that array, not the clip slot).
   */
  sessionClipAudio?: Array<{ trackIndex: number; clipIndex: number; key: string }>;
  /** Peaks of the master (shared/audio/waveform.ts), for the web app to draw. */
  waveform?: string;
  /** The master's length in milliseconds, sent with its waveform. */
  durationMs?: number;
}

export interface CompleteUploadResponse {
  seedId: string;
  title: string;
  stemCount: number;
  /**
   * For a jam commit: whether the jam's tip moved to this seed. False means
   * someone else committed first — this seed is still complete, as a sibling
   * branch off the tip it was based on; nobody's work is lost.
   */
  tipAdvanced?: boolean;
  /**
   * Pool accounting for this upload: objects the client had to PUT, objects
   * the pool already held (nothing transferred), and the bytes that saved.
   * Absent on a legacy (hash-less) upload.
   */
  blobs?: { uploaded: number; reused: number; bytesSaved: number };
  /** Preview renditions this upload stored (not counting ones that existed). */
  previews?: number;
}

export interface AbortUploadRequest {
  /** @deprecated Multipart uploads to abort — none are planned any more; still honoured for an extension that planned one before the change. */
  multiparts?: Array<{ key: string; uploadId: string }>;
  /**
   * Preview keys this upload was planned to PUT. Pool objects are never
   * deleted on abort (another seed may hold them), but a preview that no
   * completed upload ever recorded is nobody's — it is removed here rather
   * than left for the sweep.
   */
  previews?: string[];
}

// ---- Jams (branch refs) -----------------------------------------------------

/**
 * A jam is a branch: forked from `baseSeedId`, its room hydrated from that seed,
 * and `tipSeedId` advancing with every seed committed from inside it. Several
 * jams can hang off one seed. Its room is keyed by `id`, never by a seed.
 */
export interface JamSummary {
  id: string;
  baseSeedId: string;
  tipSeedId: string;
  title: string;
  createdBy: string;
  createdAt: string;
  lastSeenAt: string;
  status: "open" | "closed";
  /** Seeds committed from this jam (complete only). */
  commitCount: number;
}

export interface CreateJamRequest {
  title?: string;
}

/** Creating a jam is one row; the room is born on first open. */
export type CreateJamResponse = JamSummary;

/** Jam list item for the Multiplayer feed — JamSummary + creator display name. */
export interface JamFeedItem extends JamSummary {
  creatorUsername: string;
  /** The title of the seed the jam was forked from — what tells two jams
   *  called "Synths" apart. Null when that seed is gone; absent from older
   *  servers. */
  baseSeedTitle?: string | null;
  /** Live participants from the room's DO, if the jam is open and the room
   *  responded in time. Absent/empty for closed jams or on timeout. */
  participants?: JamParticipant[];
}

// ---- Tree -------------------------------------------------------------------

/** One commit in a tree — light: no stems, no signed URLs. */
export interface TreeNode {
  id: string;
  title: string;
  parentSeedId: string | null;
  jamId: string | null;
  note: string | null;
  version: number;
  createdAt: string;
  bpm: number;
  key: string | null;
  genre: string | null;
  producer: ApiProducer;
  childCount: number;
  /**
   * Signed preview (or master) URL, so a remix list can audition a node
   * without a second round trip. Only sent for `?audio=1` — a tree runs to
   * TREE_NODE_LIMIT nodes and the extension's hub fetches one per root in the
   * feed, which is no place to sign five hundred URLs. "" when the node has
   * no master; absent when the caller did not ask.
   */
  audioUrl?: string;
}

/** A jam drawn on the tree: a stub off its base, a marker on its tip. */
export interface JamRef {
  id: string;
  title: string;
  baseSeedId: string;
  tipSeedId: string;
  lastSeenAt: string;
  commitCount: number;
}

export interface SeedTree {
  focusId: string;
  rootId: string;
  nodes: TreeNode[];
  jams: JamRef[];
  /** True when the tree was cut at the server's node cap. */
  truncated: boolean;
}

// ---- Jam (DO envelope) ------------------------------------------------------

/** Server → client messages pushed over the jam WebSocket. */
export type JamServerMessage =
  | {
      type: "welcome";
      latestSeq: number;
      participants: JamParticipant[];
      /** This life of the room's state (v4 step 2) — the client's first sight
       * of it. A mismatch against the client's believed epoch forces a
       * hard-resync (leave + rejoin + adopt); optional only for old servers. */
      epoch?: string;
    }
  | { type: "batch"; seq: number; payload: unknown /* JamBroadcast */ }
  | { type: "presence"; participants: JamParticipant[] };

/** Which client a jam socket belongs to: the Ableton extension, or a web tab
 * (seedbed-web or the visualizer, editing). Sent as `?client=` on `/ws`; a
 * socket that names none is an extension's (extensions before 2026-10-25). */
export type JamClientKind = "extension" | "browser";

export interface JamParticipant {
  userId: string;
  username: string;
  /** The roster is keyed by user + kind (2026-10-22, finding 10): one user's
   * extension and browser tab are two entries, and a tab is labelled as one
   * rather than standing in for the person. Absent from servers before
   * 2026-10-25 and from `jam_presence` rows they wrote; read absent as
   * "extension". */
  kind?: JamClientKind;
}

/** Parse a socket's `?client=` / `X-Seedbed-Client` value; anything but
 * "browser" (absent included) is an extension. */
export function jamClientKind(value: string | null | undefined): JamClientKind {
  return value === "browser" ? "browser" : "extension";
}

/** A roster entry's identity — what React keys and dedupes should use now
 * that one user can hold two entries. */
export function participantKey(p: JamParticipant): string {
  return `${p.userId}|${p.kind ?? "extension"}`;
}

export interface JamSendResponse {
  seq: number;
}

export interface JamLogResponse {
  batches: Array<{ seq: number; payload: unknown /* JamBroadcast */ }>;
  latestSeq: number;
  /** True when `after` predates the retained log — client must re-baseline. */
  reset: boolean;
}

// ---- Jam (DO inspection) ----------------------------------------------------

/**
 * One row of the DO's `batches` table, carrying the two columns `/log` drops:
 * when it landed and who proposed it. Diagnostics only — nothing replays this.
 */
export interface JamInspectBatch {
  seq: number;
  at: string;
  fromId: string;
  /** Stored payload, verbatim. Never carries signed URLs — keys only. */
  payload: unknown;
}

/**
 * Read-only diagnostics for one room. Deliberately mints NO presigned URLs
 * (unlike /state and /log), so an inspector can poll a room without spending
 * an R2 signing round-trip per clip.
 */
export interface JamInspectResponse {
  /** False when the room has no non-`system` batch — i.e. never actually used. */
  exists: boolean;
  roomRev: number;
  tempo: number;
  tempoRev: number;
  /** The room's scene names (absent from older servers). */
  sceneNames?: Array<string | null>;
  /** The room's locators and their rev (absent from older servers). */
  cuePoints?: Array<{ name: string; time: number }>;
  cuePointsRev?: number;
  master?: { volume: number; pan: number };
  masterRev?: number;
  returns?: Array<{ name: string; mute: boolean; solo: boolean; volume: number; pan: number }>;
  returnsRev?: number;
  /** Non-lane device chains by owner ("master", "return:<i>"), each under its own rev. */
  chains?: Record<string, { devices: DeviceState[]; rev: number }>;
  /** The Set's scale (slice 44), once a Set asserted it. */
  scale?: { rootNote: number; name: string; mode: boolean; intervals: number[] };
  scaleRev?: number;
  latestSeq: number;
  oldestSeq: number | null;
  /** Omitted (undefined) when the request passed ?summary=1. */
  tracks?: CanonicalTrackState[];
  clips?: CanonicalClipState[];
  trackCount: number;
  clipCount: number;
  /** Newest-first, capped by ?limit=. Empty when ?summary=1. */
  log: JamInspectBatch[];
  /** Bounds of the retained log — the room's activity window. */
  firstBatchAt: string | null;
  lastBatchAt: string | null;
  digest: string;
  laneDigests: Record<string, string>;
  /** Live roster (observers excluded), same as /presence. */
  participants: JamParticipant[];
  /** `anonymous` (signed-out listeners, a subset of observers) do not keep
   * a room alive; absent from older servers. */
  sockets: { total: number; participants: number; observers: number; anonymous?: number };
  /** SQLite page usage of this DO's storage, in bytes. */
  storageBytes: number;
  /** See RoomStateResponse.hydration. Null before the room's first open;
   * absent from pre-hydration servers. */
  hydration?: string | null;
  /** What hydration left out of this room life, by cause (#123): clips the
   * seed never had audio for, objects gone from R2, unusable geometry. Null
   * when nothing was skipped; absent from older servers. */
  hydrationSkipped?: HydrationSkipped | null;
  /** The roomRev the last publish captured (hydration counts). Null on
   * legacy lives; roomRev above this means unpublished changes. */
  lastPublishedRoomRev?: number | null;
  /** The room's OWN activity clock (touches + newest real batch) — what
   * /expire's idle test reads. Null: never used. */
  lastActivityAt?: string | null;
  /** How many suspicious things batches have done here — a chain emptied
   * while it held devices, a lane deleted, a mass clip delete, a lane
   * proposed under a held token (2026-10-22, finding 13). Absent from
   * older servers. */
  suspicious?: number;
}

export interface JamFileSignRequest {
  /**
   * Full sha-256 hex of the clip's bytes ⇒ the shared blob pool
   * (`blobs/<hash>.wav`), where the same audio from any peer, seed or jam is
   * one object. After a PUT the client must call
   * `POST /api/jam/:jamId/files/:hash/verify` before asserting the key.
   */
  hash?: string;
  sizeBytes?: number;
  /** @deprecated Legacy client-computed `jam/<jamId>/…` key. */
  key?: string;
}

export interface JamFileSignResponse {
  /** The key the server actually signed: the requested key plus this channel's
   * prefix. Assert THIS as the clip's audioKey — the key you sent is a request,
   * not the answer, and on beta it is not where the bytes landed. */
  key: string;
  getUrl: string;
  /**
   * True ⇒ the pool already holds these bytes, verified, and this jam now
   * references them: skip the PUT and the verify, assert the key.
   */
  exists: boolean;
  /** With `exists`: the pool holds a preview too, so there is none to encode. */
  previewExists?: boolean;
  /** Present only when `exists` is false. */
  putUrl?: string;
  /** With `putUrl`: the headers the PUT must carry — see UploadFilePlan.putHeaders. */
  putHeaders?: Record<string, string>;
}

export interface JamFileVerifyResponse {
  key: string;
  getUrl: string;
  sizeBytes: number;
}

// ---- Downloads --------------------------------------------------------------

export interface DownloadHistoryEntry {
  seedId: string;
  title: string;
  bpm: number;
  key: string | null;
  downloadedAt: string;
}

/** GET /api/me/downloads — seeds the caller has imported/remixed, newest first. */
export interface DownloadHistoryResponse {
  downloads: DownloadHistoryEntry[];
}

// ---- Errors -----------------------------------------------------------------

export interface ApiError {
  error: string;
}

// ---- Extension updates ------------------------------------------------------

/**
 * GET /api/extension/version?current=<installed version>
 *
 * What the newest published build of the extension is, for the channel this
 * worker serves. Stable names the newest full release; beta the newest release
 * including prereleases. The worker reads GitHub Releases (where `just
 * release` publishes the .ablx) and caches the answer, so a release is visible
 * to clients without a backend deploy.
 *
 * Every field but `updateAvailable` is null when no release could be read —
 * the client treats that as "unknown", never as "up to date".
 */
export interface ExtensionVersionResponse {
  /** "stable" | "beta" — the worker's own channel. */
  channel: string;
  /** Newest published version, e.g. "1.4.4". */
  latest: string | null;
  /** The GitHub release page. Collaborators only while the repo is private. */
  releaseUrl: string | null;
  /**
   * Worker path that streams the release's .ablx (the repository is private,
   * so the asset is proxied rather than linked). Requires a Seedbed session.
   */
  downloadPath: string | null;
  /** The asset's file name, e.g. "Seedbed-1.4.4.ablx". */
  fileName: string | null;
  publishedAt: string | null;
  /** Release notes (Markdown, as written on the release), trimmed. */
  notes: string | null;
  /** The `current` the caller sent, or null when it sent none. */
  current: string | null;
  /** `latest` is newer than `current`. False whenever either is unknown. */
  updateAvailable: boolean;
}

/**
 * A URL the user's BROWSER can open to download the .ablx — no Authorization
 * header, because a browser navigation cannot carry one. The extension asks
 * for this (GET /api/extension/download-url/:version, session required) and
 * hands it to the desktop, so the file lands in the Downloads folder rather
 * than inside the extension's own storage directory.
 */
/**
 * `POST /api/extension/refresh` — the release workflow's hook. Re-reads GitHub
 * and replaces the channel's cached answer; the body is what `/version` will
 * now serve (with no `current`, so `updateAvailable` is always false here).
 */
export interface ExtensionRefreshResponse extends ExtensionVersionResponse {
  refreshed: true;
}

export interface ExtensionDownloadUrlResponse {
  /** Absolute, single-purpose, short-lived: carries a download-scoped ticket. */
  url: string;
  /** The version the URL serves, e.g. "1.4.4". */
  version: string;
  /** What the browser will save it as, e.g. "Seedbed-1.4.4.ablx". */
  fileName: string;
  /** How long the ticket stays valid. */
  expiresInSeconds: number;
}

// ---- The web app's feed (GET /api/feed, workers/src/feed.ts) ----------------

export type FeedTab = "discover" | "new" | "following";

/**
 * One card of the public feed: a producer's newest take on one tree. Lighter
 * than SeedSummary on purpose — no stems (a card plays the master), so a page
 * of 20 signs 20 URLs, not 200.
 */
export interface FeedCard {
  id: string;
  title: string;
  bpm: number;
  key: string | null;
  genre: string | null;
  note: string | null;
  createdAt: string;
  rootSeedId: string;
  version: number;
  jamId: string | null;
  visibility: SeedVisibility;
  producer: ApiProducer;
  /** Signed MP3 preview of the master when one exists, else the master. "" with no master. */
  audioUrl: string;
  waveform: string | null;
  durationMs: number | null;
  stemCount: number;
  playCount: number;
  likeCount: number;
  commentCount: number;
  /** Remixes and later takes hanging directly off this seed. */
  childCount: number;
  /**
   * Remixes of this SONG: how many other producers are anywhere downstream of
   * any of this producer's takes on the tree. What a card's remix count shows
   * — people, not saves.
   */
  remixCount: number;
  likedByMe: boolean;
  /** How many takes this producer has on this tree (this card is the newest). */
  takeCount: number;
  /** Every public take of this song by this producer, newest first (this card's included). */
  takes: FeedTake[];
  /** Who else made it: people credited on any of these takes (a jam's players). */
  collaborators: ApiProducer[];
  /** Set when this card is a remix of SOMEONE ELSE's seed. */
  remixOf: RemixOrigin | null;
}

/** One take behind a feed card — enough to play it and open it. */
export interface FeedTake {
  id: string;
  version: number;
  note: string | null;
  createdAt: string;
  /** Signed preview (or master) URL; "" with no master. */
  audioUrl: string;
}

export interface FeedPage {
  items: FeedCard[];
  /** Opaque; pass back as `?cursor=`. Null at the end. */
  nextCursor: string | null;
}

// ---- Profiles (GET /api/users/:handle, workers/src/users.ts) ----------------

/** A producer as a list shows them: search results, followers, credits. */
export interface ProfileSummary {
  id: string;
  handle: string;
  username: string;
  avatarUrl: string | null;
}

export interface Profile extends ProfileSummary {
  displayName: string | null;
  bio: string | null;
  createdAt: string;
  counts: {
    /** Songs: trees they started, one per tree. */
    seeds: number;
    /** Trees of someone else's they have a take on. */
    remixes: number;
    followers: number;
    following: number;
    likesReceived: number;
    playsReceived: number;
    /** Times other producers built on their seeds. */
    timesRemixed: number;
  };
  followedByMe: boolean;
  isMe: boolean;
  /** The game layer (shared/badges.ts): derived from the counts, never stored. */
  game: {
    xp: number;
    level: number;
    /** XP at which this level began, and the next one begins. */
    levelFloor: number;
    nextLevel: number;
    /** Badge ids earned, in the order shared/badges.ts lists them. */
    badges: string[];
  };
}

export type ProfileTab = "seeds" | "remixes" | "likes";

export interface SearchResult {
  seeds: FeedCard[];
  users: ProfileSummary[];
}

// ---- Social (workers/src/social.ts, notifications.ts) -----------------------

export interface LikeResponse {
  liked: boolean;
  likeCount: number;
}

export interface FollowResponse {
  following: boolean;
  followers: number;
}

export interface ApiComment {
  id: string;
  seedId: string;
  author: ProfileSummary;
  body: string;
  /** Milliseconds into the master it is pinned to; null for the seed as a whole. */
  atMs: number | null;
  createdAt: string;
  /** Whether the caller may delete it: its author, or the seed's owner. */
  canDelete: boolean;
}

export interface CommentsResponse {
  comments: ApiComment[];
}

export interface PostCommentRequest {
  body: string;
  atMs?: number | null;
}

export type NotificationKind = "remix" | "like" | "follow" | "comment" | "badge" | "send_to_live";

export interface ApiNotification {
  id: string;
  kind: NotificationKind;
  actor: ProfileSummary | null;
  seed: { id: string; title: string } | null;
  /** The comment's text, for kind "comment" (null once deleted). */
  comment: string | null;
  /** Badge id, for kind "badge". */
  detail: string | null;
  createdAt: string;
  read: boolean;
}

export interface NotificationsPage {
  items: ApiNotification[];
  nextCursor: string | null;
}

export interface UpdateSeedRequest {
  title?: string;
  description?: string | null;
  visibility?: SeedVisibility;
}

export interface UpdateProfileRequest {
  handle?: string;
  displayName?: string | null;
  bio?: string | null;
}

// ---- The game layer (workers/src/game.ts) -------------------------------------

export interface ChartProducer extends ProfileSummary {
  /** XP gained in the window (all-time: the profile's total). */
  xp: number;
}

/** Rolling windows, counted back from now: nothing resets. */
export type ChartWindow = "7d" | "30d" | "all";
export const CHART_WINDOWS: readonly ChartWindow[] = ["7d", "30d", "all"];

/** A seed card on the charts, with what earned its place in the last 7 days. */
export interface ChartSeed extends FeedCard {
  chart: {
    /** remixes × 25 + likes × 2 + one per 3 listeners — the XP rules. */
    points: number;
    /** Remixes by other producers, made in the window. */
    remixes: number;
    /** Likes from other people, in the window. */
    likes: number;
    /** Distinct listeners (never the producer) heard in the window. */
    listeners: number;
  };
}

export interface Charts {
  /** Public seeds of any age, ranked by what others did with them in the last
   * 7 days. A seed nobody touched in that time is not on the list. */
  seeds: ChartSeed[];
  /** Who gained the most XP, per window. */
  producers: Record<ChartWindow, ChartProducer[]>;
}

export interface Featured {
  card: FeedCard;
  blurb: string | null;
}

/**
 * This week's Flip: one seed, picked at random every Monday 08:00 Chicago time
 * (workers/src/flip.ts), that the web app plays behind its home screen. No
 * seed comes round again until every eligible seed has had a week.
 */
export interface Flip {
  card: FeedCard;
  /** The Monday (Chicago date, YYYY-MM-DD) this Flip's week began. */
  week: string;
  /** ISO instants: when this Flip went up, and when the next one does. */
  startsAt: string;
  endsAt: string;
}

// ---- My clips: the private clip library ("Mine" in the browser room) --------
//
// Private as a LIBRARY: only the owner lists or manages these. A clip placed
// in a project is that project's audio like any other — the room and the
// seed hold their own references to the same pool blob. (Not to be confused
// with LibraryClipEntry above: that library is a producer's seeds' own clips.)

/** Per-user storage for the clip library, counted as uploaded (WAV) bytes. */
export const LIBRARY_QUOTA_BYTES = 2 * 1024 * 1024 * 1024;
/** One clip, as uploaded — the same cap the web upload uses for a stem. */
export const LIBRARY_CLIP_MAX_BYTES = 200 * 1024 * 1024;

export type MyClipKind = "loop" | "oneshot";

/** What the producer told us about a clip (all editable later but the audio). */
export interface MyClipMeta {
  name: string;
  /** Null when nothing says — the room then plays it unwarped, at its own speed. */
  bpm: number | null;
  kind: MyClipKind;
}

/** A clip about to be uploaded: its audio's address and what it is. */
export interface NewClipManifest extends MyClipMeta {
  /** sha-256 of the WAV/AIFF bytes that will be stored. */
  hash: string;
  /** The folder the clip lands in; absent or null = the root. Must be the
   * uploader's own. */
  folderId?: string | null;
  /** Tags the browser suggests (or the producer confirmed before sending).
   * At most LIBRARY_TAG_SUGGESTIONS_MAX. */
  tags?: NewTag[];
  sizeBytes: number;
  durationMs: number;
  sampleRate: number;
  channels: number;
  sourceFormat: UploadSourceFormat;
  sourceBitDepth: number | null;
}

export interface MyClip extends MyClipMeta {
  id: string;
  ownerId: string;
  durationMs: number;
  sampleRate: number;
  channels: number;
  sourceFormat: UploadSourceFormat;
  sourceBitDepth: number | null;
  sizeBytes: number;
  createdAt: string;
  /** The library folder holding it; null = the root. */
  folderId: string | null;
  /** Live's Collections reduced to one colour: a star that travels with the account. */
  starred: boolean;
  /** Stored tags, confirmed and suggested (shared/library-tags.ts). */
  tags: MyTag[];
  /** How many saved seeds use this clip (library_clip_uses). Absent on
   * older responses; the web app reads it as 0. */
  uses?: number;
  /** The upload it arrived in (one id per /complete call); null for a kept
   * clip or one from before 0020. */
  batchId?: string | null;
  /** Live's Collections: a colour 0..6, or null. */
  color?: number | null;
  /** The pool key a placement's audioKey points at. */
  audioKey: string;
  /** Signed GET for the owner's own previews and local rooms (2 h). */
  audioUrl: string;
}

export interface MyClipsResponse {
  clips: MyClip[];
  usedBytes: number;
  quotaBytes: number;
}

export interface CreateClipsRequest {
  clips: NewClipManifest[];
}

/**
 * One plan per distinct audio this upload must send; a plan with `exists`
 * needs no PUT. Clips the owner already has (same audio) are left out and
 * named in `already`.
 */
export interface CreateClipsResponse {
  files: UploadFilePlan[];
  already: string[];
}

export interface CompleteClipsRequest {
  clips: NewClipManifest[];
  files: CompleteUploadRequest["files"];
}

export interface CompleteClipsResponse {
  clips: MyClip[];
  usedBytes: number;
  quotaBytes: number;
}

/** 413 body when an upload wouldn't fit: nothing was stored. */
export interface QuotaExceeded {
  error: string;
  usedBytes: number;
  quotaBytes: number;
  neededBytes: number;
}

export type UpdateClipRequest = Partial<MyClipMeta> & {
  /** Move it: a folder of the owner's, or null for the root. */
  folderId?: string | null;
  starred?: boolean;
  /** Live's Collections: 0..6, or null to clear. */
  color?: number | null;
  tags?: TagDelta;
};

/** How many colours a library row can wear: Live's seven Collections. */
export const LIBRARY_COLORS = 7;

/** POST /api/library/recent — a row was auditioned or placed; the account's
 * recent ring (newest 50) moves it to the front. */
export interface TouchRecentRequest {
  /** A shelf key: `mine:<clipId>` or `midi:<midiId>`. */
  key: string;
}

/** GET /api/library/clips/:id/uses — the saved seeds that use a clip. */
export interface ClipUse {
  seedId: string;
  title: string;
  createdAt: string;
  /** The seed plays an edit of the clip (split, bounced), not its own bytes. */
  derived: boolean;
}
export interface ClipUsesResponse {
  uses: ClipUse[];
}

// ---- Tags and smart folders (workers/src/library-tags.ts) ----------------------
//
// WHAT a clip is. One string per tag — `group:value` from the closed groups
// in shared/library-tags.ts, or a free word. A suggestion is a tag row the
// browser wrote with confirmed = false; only confirmed tags filter.

/** Where a tag came from: the file's name, its audio, its notes, the
 * producer, or the folder it was dropped from or moved into. */
export type TagSource = "name" | "audio" | "midi" | "user" | "folder";

export interface MyTag {
  tag: string;
  source: TagSource;
  confirmed: boolean;
}

/** A tag to store with a new item: a suggestion unless `confirmed`. */
export interface NewTag {
  tag: string;
  source: TagSource;
  confirmed?: boolean;
}

/** `set` confirms (creating as source `user` when new); `unset` removes —
 * a rejected suggestion and a removed tag are the same thing. */
export interface TagDelta {
  set?: string[];
  unset?: string[];
}

/** One multi-select: every named item takes the same delta. `suggest`
 * adds the tags as suggestions (source `folder`, unconfirmed) where the
 * item has nothing by that name — a confirmed tag is never downgraded. */
export interface BulkTagRequest extends TagDelta {
  clipIds: string[];
  midiIds: string[];
  suggest?: string[];
}

/** POST /api/library/tags/rename — one of your tags renamed, or merged into
 * another, on every row that carries it (and in every saved search). */
export interface RenameTagRequest {
  from: string;
  to: string;
}

/**
 * POST /api/library/tags/review — the suggestions of a selection answered
 * at once: `keep` confirms them (`tags` narrows to those named), else they
 * are deleted. A confirmed tag is never touched.
 */
export interface ReviewTagsRequest {
  clipIds: string[];
  midiIds: string[];
  keep: boolean;
  tags?: string[];
}

/** Live's "Add Label": a saved search over all of Mine, evaluated live. */
export interface MySmartFolder {
  id: string;
  ownerId: string;
  name: string;
  /** The search box text (`#tag` words included). */
  query: string;
  /** The shelf's chip ids (`loop`, `oneshot`, `midi`, `favorite`, `tag:…`). */
  chips: string[];
  /** A MaterialSort id, or null for the place's default. */
  sort: string | null;
  color: string | null;
  position: number;
  createdAt: string;
}

export interface CreateSmartFolderRequest {
  name: string;
  query: string;
  chips: string[];
  sort?: string | null;
}

export interface UpdateSmartFolderRequest {
  name?: string;
  query?: string;
  chips?: string[];
  sort?: string | null;
  color?: string | null;
  position?: number;
}

// ---- Folders and MIDI in the library (workers/src/library-folders.ts) -----------------
//
// WHERE, as Live's User Library and Note's Externals have it: a tree of
// folders per producer holding audio clips and MIDI. Chips and tags stay
// WHAT. One GET returns the whole library and the client builds the tree.

/** How deep the tree may go: Mine › a › b › c › d. */
export const LIBRARY_FOLDER_MAX_DEPTH = 4;

export interface MyFolder {
  id: string;
  ownerId: string;
  /** Null at the root. */
  parentId: string | null;
  name: string;
  /** `#rrggbb` or null. */
  color: string | null;
  /** Order among siblings; ties by name. */
  position: number;
  createdAt: string;
}

export interface CreateFolderRequest {
  name: string;
  parentId: string | null;
}

export interface UpdateFolderRequest {
  name?: string;
  /** Re-parent; null moves it to the root. Never itself or a descendant. */
  parentId?: string | null;
  color?: string | null;
  position?: number;
}

/** A MIDI idea kept outside any seed: the flattened, canonical note list. */
export interface MyMidi {
  id: string;
  ownerId: string;
  folderId: string | null;
  name: string;
  durationBeats: number;
  noteCount: number;
  notes: WireNote[];
  /** The tempo it was written at, for reading only — MIDI plays at the room's. */
  bpm: number | null;
  starred: boolean;
  tags: MyTag[];
  /** Live's Collections: a colour 0..6, or null. */
  color?: number | null;
  createdAt: string;
}

export interface NewMidiRequest {
  name: string;
  folderId: string | null;
  notes: WireNote[];
  durationBeats: number;
  bpm: number | null;
  tags?: NewTag[];
}

export interface UpdateMidiRequest {
  name?: string;
  bpm?: number | null;
  folderId?: string | null;
  starred?: boolean;
  color?: number | null;
  tags?: TagDelta;
}

/** Everything in the library, in one call. */
export interface MyLibraryResponse {
  folders: MyFolder[];
  clips: MyClip[];
  midi: MyMidi[];
  smartFolders: MySmartFolder[];
  usedBytes: number;
  quotaBytes: number;
  /** The account's recent ring, newest first: `mine:<id>` / `midi:<id>` keys. Absent on older responses. */
  recent?: string[];
}

/** One multi-select drag: everything named lands in `folderId` (null = root). */
export interface MoveLibraryRequest {
  clipIds: string[];
  midiIds: string[];
  folderId: string | null;
}

/** What deleting a folder did: its contents went to `movedTo`. */
export interface DeleteFolderResponse {
  ok: true;
  movedTo: string | null;
  movedFolders: number;
  movedClips: number;
  movedMidi: number;
}

// ---- Marketing showcase (workers/src/marketing.ts) -----------------------------
//
// GET /api/marketing/showcase — read by the landing page at seed-bed.com, which
// is built and deployed apart from this repo. The shape is a CONTRACT: add
// optional fields freely; renaming, removing or retyping one is a new
// `version` (and a new response, served beside the old one).

export const MARKETING_SHOWCASE_VERSION = 1;

export interface MarketingProducer {
  username: string;
  handle: string;
  /** Permanent URL, or null when the producer has no avatar. */
  avatarUrl: string | null;
}

export interface MarketingSeed {
  id: string;
  title: string;
  bpm: number;
  key: string | null;
  genre: string | null;
  durationMs: number | null;
  producer: MarketingProducer;
  stemCount: number;
  childCount: number;
  playCount: number;
  likeCount: number;
  /** Peak per slice of the master, 0–255, loudest slice = 255. Null when the
   * seed was uploaded before waveforms were recorded. */
  waveform: { buckets: number; peaks: number[] } | null;
  /** Permanent, cacheable MP3 (or the master when no preview was encoded). */
  previewAudioUrl: string;
  /** The seed's page in the web app. */
  seedUrl: string;
}

export interface MarketingTreeNode {
  id: string;
  title: string;
  /** Nearest PUBLIC ancestor; null only for the root. */
  parentSeedId: string | null;
  version: number;
  producer: MarketingProducer;
  createdAt: string;
}

export interface MarketingShowcase {
  /** Schema version — MARKETING_SHOWCASE_VERSION. */
  version: number;
  generatedAt: string;
  /** 6–8 public seeds, top of all time by the charts' engagement score. */
  seeds: MarketingSeed[];
  /** The largest public remix tree any of `seeds` belongs to. */
  remixTree: { rootId: string; nodes: MarketingTreeNode[] } | null;
}

// ---- Events (Secret Sample) — workers/src/events.ts ---------------------------

export type EventStatus = "open" | "assigned" | "closed";

export interface ApiEvent {
  slug: string;
  type: "secret_sample";
  title: string;
  blurb: string;
  status: EventStatus;
  startsAt: string | null;
  endsAt: string | null;
  discordUrl: string | null;
}

/** A seed in an event's pool. `versionId` is the exact seed row pooled — the
 * one its seed page (`/s/:versionId`) opens. */
export interface ApiEventPoolSeed {
  poolItemId: string;
  versionId: string;
  title: string;
  producer: { id: string; username: string; handle: string | null };
}

export interface ApiEventMe {
  joined: boolean;
  /** What I put in the pool (an admin may have several). */
  pooled: ApiEventPoolSeed[];
  /** The seed I was handed to flip. */
  assignment: (ApiEventPoolSeed & { assignedAt: string }) | null;
  isAdmin: boolean;
}

/** GET /api/events */
export interface ApiEventList {
  events: (ApiEvent & { createdAt: string; participantCount: number; poolCount: number })[];
  /** Whether the viewer may create and run events. */
  isAdmin: boolean;
}

/** GET /api/events/:slug */
export interface ApiEventView {
  event: ApiEvent;
  participantCount: number;
  poolCount: number;
  /** Null when signed out. */
  me: ApiEventMe | null;
}

/** A seed of mine that can go in the pool: my newest non-private version per tree. */
export interface ApiPoolableSeed {
  versionId: string;
  title: string;
  visibility: SeedVisibility;
  createdAt: string;
}

export interface ApiEventParticipantRow {
  user: { id: string; username: string; handle: string | null };
  joinedAt: string;
  pooled: ApiEventPoolSeed[];
  assignment: (ApiEventPoolSeed & { assignedAt: string }) | null;
}

/** GET /api/events/:slug/admin */
export interface ApiEventAdminView {
  event: ApiEvent;
  participants: ApiEventParticipantRow[];
  pool: (ApiEventPoolSeed & { assignedCount: number })[];
}

/** POST /api/events/:slug/assign */
export interface ApiAssignResult {
  /** Assignments made by this run (none when everyone already had one). */
  assigned: { userId: string; username: string; poolItemId: string }[];
  /** Participants still without a seed: no pooled seed but their own. */
  unassigned: { userId: string; username: string }[];
}
